/**
 * Topbar "What's new" link with a red dot when there's an unseen release.
 *
 * Reads /changelog.json once on mount (cached for the session by the
 * browser), compares latest release version to localStorage's last-seen
 * marker, and shows a small dot if there's something new. Clicking the
 * link navigates to /changelog where the page itself updates the marker.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { CHANGELOG_LAST_SEEN_KEY } from '@/pages/Changelog';

interface ReleaseMeta { version: string }
interface ChangelogJson { releases: ReleaseMeta[] }

export function WhatsNewBadge() {
  const [hasNew, setHasNew] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/changelog.json', { cache: 'no-store' })
      .then(async (r) => (r.ok ? ((await r.json()) as ChangelogJson) : null))
      .then((j) => {
        if (cancelled || !j) return;
        const latest = j.releases[0]?.version;
        if (!latest) return;
        try {
          const seen = localStorage.getItem(CHANGELOG_LAST_SEEN_KEY);
          setHasNew(seen !== latest);
        } catch {
          setHasNew(true);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Link
      to="/changelog"
      className="relative inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      title="What's new in Praxara"
    >
      <Sparkles className="h-3.5 w-3.5" />
      What's new
      {hasNew && (
        <span
          aria-label="New release available"
          className="absolute -right-0.5 -top-0.5 inline-flex h-2.5 w-2.5 rounded-full border-2 border-background bg-red-500"
        />
      )}
    </Link>
  );
}
